import React, { createContext, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { saveAnalyticsEvent } from '../../services/analytics';
import type { ArgumentType } from '../../Types/gtag.d';
import { useAuth } from './AuthProvider';

declare global {
  interface Window {
    gtag: (...args: ArgumentType[]) => void;
  }
}

// Note: you MUST prefix any AnalyticsEvents with WM_ so they show up in
// reports and maximum length for new event names is 40 characters.
export enum AnalyticsEvent {
  WM_APP_CHANGE = 'WM_APP_CHANGE',
  WM_LOGO_CLICK = 'WM_LOGO_CLICK',

  //NIGEL
  WM_NIGEL_MESSAGE = 'WM_NIGEL_MESSAGE',
  WM_NIGEL_FILE_UPLOAD = 'WM_NIGEL_FILE_UPLOAD',
  WM_NIGEL_FILE_UPLOAD_EXCEED_SIZE_LIMIT = 'WM_NIGEL_FILE_UPLOAD_EXCEED_SIZE_LIMIT',
  WM_NIGEL_FILE_UPLOAD_FAILED = 'WM_NIGEL_FILE_UPLOAD_FAILED',
  WM_NIGEL_FILE_REMOVE = 'WM_NIGEL_FILE_REMOVE',
  WM_NIGEL_FILE_TEXT_CHANGED = 'WM_NIGEL_FILE_TEXT_CHANGED',
  WM_NIGEL_REGENERATE_RESPONSE = 'WM_NIGEL_REGENERATE_RESPONSE',
  WM_NIGEL_ABORT_RESPONSE = 'WM_NIGEL_ABORT_RESPONSE',
  WM_NIGEL_CHAT_START_FROM_HERE = 'WM_NIGEL_START_FROM_HERE',
  WM_NIGEL_LLM_CHANGE = 'WM_NIGEL_LLM_CHANGE',
  WM_NIGEL_FEEDBACK_BUTTON_CLICK = 'WM_NIGEL_FEEDBACK_BUTTON_CLICK',
  WM_NIGEL_PAGE_LOAD = 'WM_NIGEL_PAGE_LOAD',
  WM_NIGEL_LOAD_MORE_CONVERSATIONS = 'WM_NIGEL_LOAD_MORE_CONVERSATIONS',
  WM_NIGEL_LOAD_MORE_FOLDERS = 'WM_NIGEL_LOAD_MORE_FOLDERS',
  WM_NIGEL_LOAD_MORE_SEARCH_RESULTS = 'WM_NIGEL_LOAD_MORE_SEARCH_RESULTS',
  WM_NIGEL_OPEN_CLOSE_LEFT_PANEL = 'WM_NIGEL_OPEN_CLOSE_LEFT_PANEL',
  WM_NIGEL_CAROUSEL_NAVIGATION_NEXT = 'WM_NIGEL_CAROUSEL_NAVIGATION_NEXT',
  WM_NIGEL_CAROUSEL_NAVIGATION_PREV = 'WM_NIGEL_CAROUSEL_NAVIGATION_PREV',
  WM_NIGEL_NEW_CHAT = 'WM_NIGEL_NEW_CHAT',
  WM_NIGEL_NEW_PRIVATE_CHAT = 'WM_NIGEL_NEW_PRIVATE_CHAT',
  WM_NIGEL_DEFAULT_FOLDER = 'WM_NIGEL_DEFAULT_FOLDER',
  WM_NIGEL_PINNED_CHATS = 'WM_NIGEL_PINNED_CHATS',
  WM_NIGEL_SEARCH_FILTERING = 'WM_NIGEL_SEARCH_FILTERING',
  WM_NIGEL_CONVERSATION_CLICK = 'WM_NIGEL_CONVERSATION_CLICK',
  WM_NIGEL_CONVERSATION_RENAME = 'WM_NIGEL_CONVERSATION_RENAME',
  WM_NIGEL_CONVERSATION_RENAME_CONFIRM = 'WM_NIGEL_CONVERSATION_RENAME_CONFIRM',
  WM_NIGEL_CONVERSATION_SHARED_LINK = 'WM_NIGEL_CONVERSATION_SHARED_LINK',
  WM_NIGEL_CONVERSATION_SHARED_LINK_CHECK = 'WM_NIGEL_CONVERSATION_SHARED_LINK_CHECK',
  WM_NIGEL_CONVERSATION_SHARED_LINK_COPIED = 'WM_NIGEL_CONVERSATION_SHARED_LINK_COPIED',
  WM_NIGEL_CONVERSATION_SHARED_LINK_CLOSE = 'WM_NIGEL_CONVERSATION_SHARED_LINK_CLOSE',
  WM_NIGEL_CONVERSATION_DOWNLOAD = 'WM_NIGEL_CONVERSATION_DOWNLOAD',
  WM_NIGEL_CONVERSATION_REUSE_PROMPT = 'WM_NIGEL_CONVERSATION_REUSE_PROMPT',
  WM_NIGEL_CONVERSATION_DELETE = 'WM_NIGEL_CONVERSATION_DELETE',
  WM_NIGEL_CONVERSATION_DELETE_CONFIRM = 'WM_NIGEL_CONVERSATION_DELETE_CONFIRM',
  WM_NIGEL_CONVERSATION_PINNED = 'WM_NIGEL_CONVERSATION_PINNED',
  WM_NIGEL_CONVERSATION_PRIVACY_TOGGLE = 'WM_NIGEL_CONVERSATION_PRIVACY_TOGGLE',
  WM_NIGEL_CONVERSATION_MOVE_OPENED = 'WM_NIGEL_CONVERSATION_MOVE_OPENED',
  WM_NIGEL_CONVERSATION_MOVED = 'WM_NIGEL_CONVERSATION_MOVED',
  WM_NIGEL_FOLDER_CREATE = 'WM_NIGEL_FOLDER_CREATE',
  WM_NIGEL_FOLDER_CREATE_CONFIRM = 'WM_NIGEL_FOLDER_CREATE_CONFIRM',
  WM_NIGEL_FOLDER_NAME_CLICK = 'WM_NIGEL_FOLDER_NAME_CLICK',
  WM_NIGEL_FOLDER_CLICK = 'WM_NIGEL_FOLDER_CLICK',
  WM_NIGEL_FOLDER_EDIT = 'WM_NIGEL_FOLDER_EDIT',
  WM_NIGEL_FOLDER_SHARED = 'WM_NIGEL_FOLDER_SHARED',
  WM_NIGEL_FOLDER_DELETE = 'WM_NIGEL_FOLDER_DELETE',
  WM_NIGEL_FOLDER_DELETE_CONFIRM = 'WM_NIGEL_FOLDER_DELETE_CONFIRM',
  WM_NIGEL_FOLDER_MOVE_CONTENTS = 'WM_NIGEL_FOLDER_MOVE_CONTENTS',
  WM_NIGEL_FOLDER_PINNED = 'WM_NIGEL_FOLDER_PINNED',
  WM_NIGEL_FOLDER_VIEW_CHANGE = 'WM_NIGEL_FOLDER_VIEW_CHANGE',
  WM_NIGEL_FOLDER_DUPLICATE = 'WM_NIGEL_FOLDER_DUPLICATE',
  WM_NIGEL_GLOBAL_SEARCH = 'WM_NIGEL_GLOBAL_SEARCH',
  WM_NIGEL_NAVBAR_ROUTE_LINKS = 'WM_NIGEL_NAVBAR_ROUTE_LINKS',
  WM_NIGEL_NAVBAR_PROFILE_SETTINGS = 'WM_NIGEL_NAVBAR_PROFILE_SETTINGS',
  WM_NIGEL_NAVBAR_ADMIN = 'WM_NIGEL_NAVBAR_ADMIN',
  WM_NIGEL_NAVBAR_RELEASE_NOTES = 'WM_NIGEL_NAVBAR_RELEASE_NOTES',
  WM_NIGEL_NAVBAR_VIEW_MY_FILES = 'WM_NIGEL_NAVBAR_VIEW_MY_FILES',
  WM_NIGEL_NAVBAR_LOGOUT = 'WM_NIGEL_NAVBAR_LOGOUT',
  WM_NIGEL_HOME_TRENDING_PROMPTS = 'WM_NIGEL_HOME_TRENDING_PROMPTS',
  WM_NIGEL_CHAT_ATTACH_DOCUMENTS = 'WM_NIGEL_CHAT_ATTACH_DOCUMENTS',
  WM_NIGEL_UPLOAD_DIALOG_CLOSE = 'WM_NIGEL_UPLOAD_DIALOG_CLOSE',
  WM_NIGEL_UPLOAD_DIALOG_CONFIRM = 'WM_NIGEL_UPLOAD_DIALOG_CONFIRM',
  WM_NIGEL_UPLOAD_DIALOG_UPLOAD_FILES = 'WM_NIGEL_UPLOAD_DIALOG_UPLOAD_FILES',
  WM_NIGEL_MY_FILES_ADD_MORE = 'WM_NIGEL_MY_FILES_ADD_MORE',
  WM_NIGEL_MY_FILES_UPLOAD_DIALOG_CLOSE = 'WM_NIGEL_MY_FILES_UPLOAD_DIALOG_CLOSE',
  WM_NIGEL_MY_FILES_TOGGLE_PIN = 'WM_NIGEL_MY_FILES_TOGGLE_PIN',
  WM_NIGEL_MY_FILES_RENAME = 'WM_NIGEL_MY_FILES_RENAME',
  WM_NIGEL_MY_FILES_DELETE = 'WM_NIGEL_MY_FILES_DELETE',
  WM_NIGEL_MY_FILES_COPY_TO = 'WM_NIGEL_MY_FILES_COPY_TO',
  WM_NIGEL_PROFILE_SETTINGS_DEFAULT_LLM = 'WM_NIGEL_PROFILE_SETTINGS_DEFAULT_LLM',
  WM_NIGEL_PROFILE_SETTINGS_NAME_EDIT = 'WM_NIGEL_PROFILE_SETTINGS_NAME_EDIT',
  WM_NIGEL_PROFILE_SETTINGS_NAME_SAVE = 'WM_NIGEL_PROFILE_SETTINGS_NAME_SAVE',
  WM_NIGEL_PROFILE_SETTINGS_TEMP = 'WM_NIGEL_PROFILE_SETTINGS_TEMP',
  WM_NIGEL_PROMPT_CLICK = 'WM_NIGEL_PROMPT_CLICK',
  WM_NIGEL_PROMPT_UPDATE = 'WM_NIGEL_PROMPT_UPDATE',
  WM_NIGEL_PROMPT_ARCHIVE = 'WM_NIGEL_PROMPT_ARCHIVE',
  WM_NIGEL_PROMPT_COPY = 'WM_NIGEL_PROMPT_COPY',
  WM_NIGEL_PROMPT_CREATE = 'WM_NIGEL_PROMPT_CREATE',
  WM_NIGEL_PROMPT_SHARE = 'WM_NIGEL_PROMPT_SHARE',
  WM_NIGEL_OPEN_PROMPT_DRAWER = 'WM_NIGEL_OPEN_PROMPT_DRAWER',
  WM_NIGEL_CHAT_FEEDBACK = 'WM_NIGEL_CHAT_FEEDBACK',
  //Search
  WM_NIGEL_FOLDER_SEARCH = 'WM_NIGEL_FOLDER_SEARCH',
  WM_NIGEL_ADVANCED_SEARCH = 'WM_NIGEL_ADVANCED_SEARCH',
  WM_NIGEL_LEFT_NAV_SEARCH = 'WM_NIGEL_LEFT_NAV_SEARCH',
  //Deep Research
  WM_NIGEL_DEEP_RESEARCH_STARTED = 'WM_NIGEL_DEEP_RESEARCH_STARTED',
  // Intellio Advantage
  WM_IA_TAB_VIEW = 'WM_IA_TAB_VIEW',
  WM_IA_PROJECT_STATUS_CLICK = 'WM_IA_PROJECT_STATUS_CLICK',
  WM_IA_PROJECT_OVERVIEW_VIEW_MORE = 'WM_IA_PROJECT_OVERVIEW_VIEW_MORE',
  WM_IA_PROJECT_OVERVIEW_KPI_VIEW_MORE = 'WM_IA_PROJECT_OVERVIEW_KPI_VIEW_MORE',
  WM_IA_PROJECT_OVERVIEW_KPI_ADD = 'WM_IA_PROJECT_OVERVIEW_KPI_ADD_QUESTION',
  WM_IA_PROJECT_OVERVIEW_KPI_SAVE = 'WM_IA_PROJECT_OVERVIEW_KPI_SAVE',
  WM_IA_PROJECT_OVERVIEW_KPI_EXPAND = 'WM_IA_PROJECT_OVERVIEW_KPI_EXPAND',
  WM_IA_PROJECT_OVERVIEW_KEY_DELETE = 'WM_IA_PROJECT_OVERVIEW_KEY_DELETE',
  WM_IA_PROJECT_OVERVIEW_KEY_SAVE = 'WM_IA_PROJECT_OVERVIEW_KEY_SAVE',
  WM_IA_PROJECT_OVERVIEW_KEY_ADD = 'WM_IA_PROJECT_OVERVIEW_KEY_ADD',
  //Search
  WM_IA_FOLDER_SEARCH = 'WM_IA_FOLDER_SEARCH',
  WM_IA_ADVANCED_SEARCH = 'WM_IA_ADVANCED_SEARCH',
  WM_IA_LEFT_NAV_SEARCH = 'WM_IA_LEFT_NAV_SEARCH',
  //Prompt Builder
  WM_NIGEL_PROMPT_STEP_BUILDER_CLICK = 'WM_NIGEL_PROMPT_STEP_BUILDER_CLICK',
  WM_NIGEL_PROMPT_QUICK_CREATE_CLICK = 'WM_NIGEL_PROMPT_QUICK_CREATE_CLICK',
  WM_NIGEL_CREATE_EXTERNAL_PROMPT_CLICK = 'WM_NIGEL_CREATE_EXTERNAL_PROMPT_CLICK',
  // AI Store
  WM_NIGEL_AI_STORE_ACCESS = 'WM_NIGEL_AI_STORE_ACCESS',
  WM_NIGEL_AI_STORE_SEARCH = 'WM_NIGEL_AI_STORE_SEARCH',
  WM_NIGEL_AI_STORE_FILTER = 'WM_NIGEL_AI_STORE_FILTER',
  WM_NIGEL_AI_STORE_SOLUTION_VIEW = 'WM_NIGEL_AI_STORE_SOLUTION_VIEW',
  WM_NIGEL_AI_STORE_SOLUTION_ACCESS = 'WM_NIGEL_AI_STORE_SOLUTION_ACCESS',
  // Question Library - Collections
  WM_IA_QL_ADD_COLLECTION = 'WM_IA_QL_ADD_COLLECTION',
  WM_IA_QL_EDIT_COLLECTION = 'WM_IA_QL_EDIT_COLLECTION',
  WM_IA_QL_DELETE_COLLECTION = 'WM_IA_QL_DELETE_COLLECTION',
  WM_IA_QL_VISIBILITY_COLLECTION = 'WM_IA_QL_VISIBILITY_COLLECTION',
  WM_IA_QL_SHARE_COLLECTION = 'WM_IA_QL_SHARE_COLLECTION',
  WM_IA_QL_COLLECTION_CUSTOM_PROMPT = 'WM_IA_QL_COLLECTION_CUSTOM_PROMPT',
  // Question Library - Categories
  WM_IA_QL_ADD_CATEGORY = 'WM_IA_QL_ADD_CATEGORY',
  WM_IA_QL_EDIT_CATEGORY = 'WM_IA_QL_EDIT_CATEGORY',
  WM_IA_QL_DELETE_CATEGORY = 'WM_IA_QL_DELETE_CATEGORY',
  WM_IA_QL_CATEGORY_CUSTOM_PROMPT = 'WM_IA_QL_CATEGORY_CUSTOM_PROMPT',
  // Question Library - Questions
  WM_IA_QL_ADD_QUESTION = 'WM_IA_QL_ADD_QUESTION',
  WM_IA_QL_EDIT_QUESTION = 'WM_IA_QL_EDIT_QUESTION',
  WM_IA_QL_DELETE_QUESTION = 'WM_IA_QL_DELETE_QUESTION',
  WM_IA_QL_QUESTION_CUSTOM_PROMPT = 'WM_IA_QL_QUESTION_CUSTOM_PROMPT',
  // Project Config
  WM_IA_PROJECT_CONFIG_TAB_VIEW = 'WM_IA_PROJECT_CONFIG_TAB_VIEW',
  WM_IA_PROJECT_CONFIG_KPI_FEEDBACK = 'WM_IA_PROJECT_CONFIG_KPI_FEEDBACK',
  WM_IA_PROJECT_CONFIG_KPI_DEFAULT_ADD = 'WM_IA_PROJECT_CONFIG_KPI_DEFAULT_ADD',
  WM_IA_PROJECT_CCONFIG_KEY_ADD = 'WM_IA_PROJECT_CCONFIG_KEY_ADD',
  WM_IA_PROJECT_CONFIG_KEY_DELETE = 'WM_IA_PROJECT_CONFIG_KEY_DELETE',
  WM_IA_PROJECT_CONFIG_KEY_SAVE = 'WM_IA_PROJECT_CONFIG_KEY_SAVE',
  // Home
  WM_IA_HOME_TEXT_GLEAN_INFO = 'WM_IA_HOME_TEXT_GLEAN_INFO',
  WM_IA_HOME_TEXT_FAQ = 'WM_IA_HOME_TEXT_FAQ',
  WM_IA_HOME_TEXT_USER_GUIDE = 'WM_IA_HOME_TEXT_USER_GUIDE',
  WM_IA_HOME_SEARCH = 'WM_IA_HOME_SEARCH',
  WM_IA_HOME_CREATE_PROJECT = 'WM_IA_HOME_CREATE_PROJECT',
  WM_IA_HOME_PROJECT_CLICK = 'WM_IA_HOME_PROJECT_CLICK',
  WM_IA_HOME_ARCHIVE = 'WM_IA_HOME_ARCHIVE',
  WM_IA_HOME_SHOW_ARCHIVE = 'WM_IA_HOME_SHOW_ARCHIVE',
  WM_IA_HOME_SHOW_ALL_PROJECTS = 'WM_IA_HOME_SHOW_ALL_PROJECTS',
  WM_IA_HOME_CONTENT_EXPANDED = 'WM_IA_HOME_CONTENT_EXPANDED',
  // Discovery
  WM_IA_D_PAGE_LOAD_TIME = 'WM_IA_D_PAGE_LOAD_TIME',
  WM_IA_D_CATEGORY_ACCORDION = 'WM_IA_D_CATEGORY_ACCORDION',
  WM_IA_D_QUESTION_CLICK = 'WM_IA_D_QUESTION_CLICK',
  WM_IA_D_RESCAN_DOCUMENTS = 'WM_IA_D_RESCAN_DOCUMENTS',
  WM_IA_D_VALIDATE = 'WM_IA_D_VALIDATE',
  WM_IA_D_SOURCE_CLICK = 'WM_IA_D_SOURCE_CLICK',
  WM_IA_D_SHOW_ALL_SOURCES = 'WM_IA_D_SHOW_ALL_SOURCES',
  WM_IA_D_EXPAND_ALL_CATEGORIES = 'WM_IA_D_EXPAND_ALL_CATEGORIES',
  WM_IA_D_QUESTION_FILTER = 'WM_IA_D_QUESTION_FILTER',
  WM_IA_D_RUN_PROJECT = 'WM_IA_D_RUN_PROJECT',
  WM_IA_D_SEARCH_ENTERED = 'WM_IA_D_SEARCH_ENTERED',
  WM_IA_D_RESCAN_QUESTIONS = 'WM_IA_D_RESCAN_QUESTIONS',
  WM_IA_D_TAB_VIEW = 'WM_IA_D_TAB_VIEW',
  WM_IA_D_GENERATE_REPORT = 'WM_IA_D_GENERATE_REPORT',
  WM_IA_D_OPEN_RUN_DIALOG = 'WM_IA_D_OPEN_RUN_DIALOG',
  WM_IA_D_RUN_SELECTED = 'WM_IA_D_RUN_SELECTED',
  WM_IA_D_QUESTION_FEEDBACK = 'WM_IA_D_QUESTION_FEEDBACK',
  WM_IA_D_QUESTION_EXPAND = 'WM_IA_D_QUESTION_EXPAND',
  WM_IA_D_QUESTION_VERSION_CHANGE = 'WM_IA_D_QUESTION_VERSION_CHANGE',
  WM_IA_D_KEY_QUESTIONS_DOWNLOAD = 'WM_IA_D_KEY_QUESTIONS_DOWNLOAD',
  WM_IA_D_CONFLICT_REPORT_PROCESS = 'WM_IA_D_CONFLICT_REPORT_PROCESS',
  // Analysis Events
  WM_IA_A_TAB_STEP = 'WM_IA_A_TAB_STEP',
  WM_IA_A_VALIDATE = 'WM_IA_A_VALIDATE',
  WM_IA_A_PREVIOUS_CLICK = 'WM_IA_A_PREVIOUS_CLICK',
  WM_IA_A_NEXT_CLICK = 'WM_IA_A_NEXT_CLICK',
  WM_IA_A_FINISH_CLICK = 'WM_IA_A_FINISH_CLICK',
  WM_IA_A_FEEDBACK_CLICK = 'WM_IA_A_FEEDBACK_CLICK',
  WM_IA_A_QUESTION_EDIT = 'WM_IA_A_QUESTION_EDIT',
  WM_IA_A_QUESTION_SAVE = 'WM_IA_A_QUESTION_SAVE',
  WM_IA_A_APPLY_CHANGES = 'WM_IA_A_APPLY_CHANGES',
  WM_IA_A_APPLY_CHANGES_CONFIRM = 'WM_IA_A_APPLY_CHANGES_CONFIRM',
  WM_IA_A_APPLY_CHANGES_DIALOG_CLOSE = 'WM_IA_A_APPLY_CHANGES_DIALOG_CLOSE',
  WM_IA_A_QUESTION_GENERATE = 'WM_IA_A_QUESTION_GENERATE',
  WM_IA_A_QUESTION_REPROMPT = 'WM_IA_A_QUESTION_REPROMPT',
  WM_IA_A_GENERATE_PAGE = 'WM_IA_A_GENERATE_PAGE',
  WM_IA_A_MODIFY_TONE = 'WM_IA_A_MODIFY_TONE',
  WM_IA_A_SOURCE_CLICK = 'WM_IA_A_SOURCE_CLICK',
  WM_IA_A_SHOW_ALL_SOURCES = 'WM_IA_A_SHOW_ALL_SOURCES',
  WM_IA_A_GENERATE_REPORT = 'WM_IA_A_GENERATE_REPORT',
  WM_IA_A_GENERATE_PRESENTATION = 'WM_IA_A_GENERATE_PRESENTATION',
  WM_IA_A_ADD_MORE_FILES = 'WM_IA_A_ADD_MORE_FILES',
  // Scope Area
  WM_IA_A_SCOPE_AREA_NEW_RUN = 'WM_IA_A_SCOPE_AREA_NEW_RUN',
  WM_IA_A_SCOPE_AREA_VIEW = 'WM_IA_A_SCOPE_AREA_VIEW',
  // Site Visit Notes
  WM_IA_A_SITE_NOTES_SAVE = 'WM_IA_A_SITE_NOTES_SAVE',
  WM_IA_A_SITE_NOTES_PROCESS_PROJECT = 'WM_IA_A_SITE_NOTES_PROCESS_PROJECT',
  WM_IA_A_SITE_NOTES_DIALOG_CONTINUE = 'WM_IA_A_SITE_NOTES_DIALOG_CONTINUE',
  WM_IA_A_SITE_NOTES_DIALOG_CLOSE = 'WM_IA_A_SITE_NOTES_DIALOG_CLOSE',
  // KPI
  WM_IA_A_KPI_QUESTION_ACCORDION = 'WM_IA_A_KPI_QUESTION_ACCORDION',
  WM_IA_A_KPI_QUESTION_VERSION_CHANGE = 'WM_IA_A_KPI_QUESTION_VERSION_CHANGE',
  WM_IA_A_KPI_EXPAND_ALL = 'WM_IA_A_KPI_EXPAND_ALL',
  WM_IA_A_KPI_DELETE = 'WM_IA_A_KPI_DELETE',
  WM_IA_A_KPI_CONFIRM = 'WM_IA_A_KPI_CONFIRM',
  // RUBRIC
  WM_IA_A_RUBRIC_QUESTION_ACCORDION = 'WM_IA_A_RUBRIC_QUESTION_ACCORDION',
  WM_IA_A_RUBRIC_QUESTION_VERSION_CHANGE = 'WM_IA_A_RUBRIC_QUESTION_VERSION_CHANGE',
  WM_IA_A_RUBRIC_EXPAND_ALL = 'WM_IA_A_RUBRIC_EXPAND_ALL',
  WM_IA_A_RUBRIC_DOWNLOAD = 'WM_IA_A_RUBRIC_DOWNLOAD',
  // BENCHMARKS
  WM_IA_A_BENCHMARK_PAGE_LOAD = 'WM_IA_A_BENCHMARK_PAGE_LOAD',
  WM_IA_A_BENCHMARK_SELECTION = 'WM_IA_A_BENCHMARK_SELECTION',
  WM_IA_A_BENCHMARK_REFRESH = 'WM_IA_A_BENCHMARK_REFRESH',
  WM_IA_A_BENCHMARK_APPLY = 'WM_IA_A_BENCHMARK_APPLY',
  // RUBRIC SUMMARY
  // FLASH_REPORT
  WM_IA_A_FR_ADD_QUESTION = 'WM_IA_A_FR_ADD_QUESTION',
  WM_IA_A_FR_DELETE_QUESTION = 'WM_IA_A_FR_DELETE_QUESTION',
  // Question ANSWER
  WM_IA_A_QA_QUESTION_VERSION_CHANGE = 'WM_IA_A_QA_QUESTION_VERSION_CHANGE',
  // PROMPT ANSWER
  WM_IA_A_PA_QUESTION_VERSION_CHANGE = 'WM_IA_A_PA_QUESTION_VERSION_CHANGE',
  //CHAT
  WM_IA_CHAT_NEW = 'WM_IA_CHAT_NEW',
  WM_IA_CHAT_SEND_MESSAGE = 'WM_IA_CHAT_SEND_MESSAGE',
  WM_IA_CHAT_SITE_NOTES_FILTER = 'WM_IA_CHAT_SITE_NOTES_FILTER',
  WM_IA_CHAT_SITE_NOTES_EDIT = 'WM_IA_CHAT_SITE_NOTES_EDIT',
  WM_IA_CHAT_SITE_NOTES_EDIT_SAVE = 'WM_IA_CHAT_SITE_NOTES_EDIT_SAVE',
  WM_IA_CHAT_REGENERATE_RESPONSE = 'WM_IA_CHAT_REGENERATE_RESPONSE',
  WM_IA_CHAT_START_FROM_HERE = 'WM_IA_CHAT_START_FROM_HERE',
  WM_IA_CHAT_SOURCE_CLICK = 'WM_IA_CHAT_SOURCE_CLICK',
  WM_IA_CHAT_FEEDBACK = 'WM_IA_CHAT_FEEDBACK',
  WM_IA_CONVERSATION_PRIVACY_TOGGLE = 'WM_IA_CONVERSATION_PRIVACY_TOGGLE',
  //PROJECT CONFIG
  WM_IA_P_CONFIG = 'WM_IA_P_CONFIG',
  WM_IA_P_CONFIG_SOW_EXTRACTIONS = 'WM_IA_P_CONFIG_SOW_EXTRACTIONS',
  //DATA REQUEST ITEMS
  WM_IA_DATA_REQUEST_LIST_EXPORT = 'WM_IA_DATA_REQUEST_LIST_EXPORT',
  // Intellio Insights
  WM_INTELLIO_INSIGHTS_LINK_CLICK = 'WM_INTELLIO_INSIGHTS_LINK_CLICK',
  // IA STARTER PROMPTS
  WM_IA_STARTER_CONVERSATION_CLICK = 'WM_IA_STARTER_CONVERSATION_CLICK',
}

// HEY!! Are you adding a new property here? Make sure to add it as a Google Analytics Custom Dimension and
// to the report pulling python script in databricks
export type AnalyticsEventData = {
  // An ID associated with the event to trace back to our database, if applicable
  id?: string;
  // The value, if applicable
  value?: string;
  // The type related to the event, if applicable
  type?: string;
  // The size related to the event, if applicable
  size?: string;
};

export type AnalyticsEventVerboseData = {
  //User Defaults
  oldLLM?: string;
  newLLM?: string;
  oldTemperature?: number;
  newTemperature?: number;
  oldPreferredName?: string;
  newPreferredName?: string;

  //Page Navigation
  newPage?: string;
  leftPanelWasOpen?: boolean;
  leftPanelNowOpen?: boolean;
  pageLoadTime?: string;
  oldApp?: string;
  newApp?: string;

  //Search/Filter
  searchQuery?: string;
  oldShouldShowArchived?: boolean;
  newShouldShowArchived?: boolean;
  oldShouldShowAllProjects?: boolean;
  newShouldShowAllProjects?: boolean;
  selectedStatuses?: string[];
  searchOpened?: boolean;
  searchFolders?: boolean;
  searchConversations?: boolean;

  //Message & Conversation
  messageId?: string;
  oldMessageId?: string;
  conversationId?: string;
  newConversationId?: string;
  oldConversationId?: string;
  attachedFileIds?: string[];
  llmUsed?: string;
  conversationWasShareable?: boolean;
  conversationNowShareable?: boolean;
  conversationOldName?: string;
  conversationNewName?: string;
  numConversationsOld?: number;
  conversationWasPinned?: boolean;
  conversationNowPinned?: boolean;
  conversationWasPrivate?: boolean;
  conversationNowPrivate?: boolean;
  oldFolderId?: string;
  newFolderId?: string;

  //Folders
  folderId?: string;
  oldFolderName?: string;
  newFolderName?: string;
  numFoldersOld?: number;
  folderWasPinned?: boolean;
  folderNowPinned?: boolean;
  folderOldView?: string;
  folderNewView?: string;
  contributors?: string[];

  //Carousel
  nextSlide?: number;
  prevSlide?: number;

  //Prompts
  promptId?: string;
  promptTrendingIndex?: number;
  promptView?: string;
  isExternal?: boolean;

  //Files
  fileId?: string;
  fileName?: string;
  oldFileName?: string;
  newFileName?: string;
  fileSize?: number;
  fileType?: string;
  fileWasPinned?: boolean;
  fileNowPinned?: boolean;
  numFiles?: number;
  newFileText?: string;

  //Projects
  projectId?: string;
  runId?: string;
  scope?: string;
  oldIsArchived?: boolean;
  newIsArchived?: boolean;
  allCategoriesWereExpanded?: boolean;
  allCategoriesNowExpanded?: boolean;
  iaView?: string;
  referenceSelected?: string;
  referenceUrlSelected?: string;

  //Analysis
  newTone?: string;
  pageName?: string;
  startNewRun?: boolean;
  wasEditingResponse?: boolean;
  nowEditingResponse?: boolean;
  newPrompt?: string;

  //Discovery
  oldRunId?: string;
  newRunId?: string;
  projectOldView?: string;
  projectNewView?: string;
  scopeWasSelected?: boolean;
  scopeNowSelected?: boolean;
  oldScopeNotes?: string;
  newScopeNotes?: string;
  selectedScopes?: string[];
  selectedCollections?: string[];
  discoveryView?: string;
  oldDiscoveryView?: string;
  newDiscoveryView?: string;
  categoryName?: string;
  numQuestionsInCategory?: number;
  numQuestionsInCategoryAnswered?: number;
  numQuestionsInCategoryMoreInfoNeeded?: number;
  numQuestionsInCategoryNotFound?: number;
  numQuestionsInCategorySkipped?: number;
  numQuestionsInCategoryPending?: number;

  //Questions
  questionId?: string;
  oldVersionId?: string;
  newVersionId?: string;
  questionIds?: string[];
  numChildQuestions?: number;
  questionStatus?: string;
  oldQuestionStatus?: string;
  newQuestionStatus?: string;
  status?: string;
  statusWasSelected?: boolean;
  statusNowSelected?: boolean;
  isExpanded?: boolean;

  // Question Library
  collectionId?: string;
  collectionName?: string;
  collectionVisibility?: string;
  collectionTags?: string[];
  collectionShared?: string[];

  //Feedback
  thumbsUp?: boolean;
  otherFeedback?: string;
  suggestedFeedback?: string;

  //Home
  oldHomeContentExpanded?: string;
  newHomeContentExpanded?: string;

  //Archiving
  archived?: boolean;

  // Benchmarks
  benchmarkSelection?: string;

  // Intellio Insights
  linkUrl?: string;
  linkName?: string;
  cardLocation?: string;
};

export type AnalyticsMetaData = {
  pageLocation: string;
  pageTitle: string;
  pageReferrer: string;
  screenResolution: string;
  userAgent: string;
  language: string;
  geoLocation?: string;
};

type SessionOptions = {
  appUserId: string;
  appSessionId: string;
};

type SessionStorage = {
  value: SessionOptions;
};

interface AnalyticsContextType {
  event: (
    eventType: AnalyticsEvent,
    data?: AnalyticsEventData,
    verboseData?: AnalyticsEventVerboseData
  ) => void;
}

interface Props {
  children: React.ReactNode;
}

const googleAnalyticsEvent = (
  event: AnalyticsEvent,
  timestamp: string,
  data?: AnalyticsEventData,
  session?: SessionOptions
): void => {
  window.gtag('event', event.toString(), {
    ...data,
    ...session,
    timestamp: timestamp,
  });
};

const getGeoLocation = async () => {
  if (import.meta.env.VITE_ENVIRONMENT !== 'prod') {
    return null;
  }

  let location = sessionStorage.getItem('geoLocation');
  if (location) {
    return location;
  }

  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();

    if (!data || data.error) {
      return null;
    }

    location = JSON.stringify({
      country: data.country_name,
      region: data.region,
      city: data.city,
      latitude: data.latitude,
      longitude: data.longitude,
    });

    sessionStorage.setItem('geoLocation', location);

    return location;
  } catch {
    return null;
  }
};

const trackAnalyticEvent = async (
  event: AnalyticsEvent,
  timestamp: string,
  data?: AnalyticsEventData,
  verboseData?: AnalyticsEventVerboseData,
  session?: SessionOptions
): Promise<void> => {
  const pageLocation = window.location.href;
  const pageReferrer = document.referrer;
  const pageTitle = document.title;
  const screenResolution = `${window.screen.width}x${window.screen.height}`;
  const userAgent = navigator.userAgent;
  const language = navigator.language;
  const geoLocation = await getGeoLocation();

  const metadata = {
    pageReferrer,
    screenResolution,
    userAgent,
    language,
  } as AnalyticsMetaData;

  if (geoLocation) {
    metadata.geoLocation = geoLocation;
  }

  saveAnalyticsEvent(
    event,
    timestamp,
    metadata,
    pageLocation,
    pageTitle,
    data,
    verboseData,
    session?.appSessionId
  );
};

export const AnalyticsHelper = (): AnalyticsContextType => {
  function getStorage(): SessionStorage | null {
    const value: string | null = sessionStorage.getItem('_session');
    const result: SessionStorage | null = value ? (JSON.parse(value) as SessionStorage) : null;

    return result;
  }

  function setStorage(userId: string, sessionId: string): SessionStorage {
    const storage: SessionStorage = {
      value: {
        appUserId: userId,
        appSessionId: sessionId,
      },
    };
    sessionStorage.setItem('_session', JSON.stringify(storage));

    const timestamp = new Date().toISOString();
    googleAnalyticsEvent(AnalyticsEvent.WM_NIGEL_PAGE_LOAD, timestamp, undefined, storage.value);
    trackAnalyticEvent(
      AnalyticsEvent.WM_NIGEL_PAGE_LOAD,
      timestamp,
      undefined,
      undefined,
      storage.value
    );

    return storage;
  }

  const [session, setSession] = useState<SessionOptions>();
  const { initialized, currentUser } = useAuth();

  useEffect(() => {
    if (!initialized) {
      return;
    }

    if (currentUser?.id) {
      let storageData = getStorage();
      let sessionId = '';

      if (!storageData || storageData.value.appSessionId === '') {
        sessionId = uuidv4();
      } else {
        sessionId = storageData.value.appSessionId;
      }

      storageData = setStorage(currentUser.id, sessionId);
      setSession(storageData.value);
    }
  }, [initialized, currentUser?.id]);

  const contextType: AnalyticsContextType = {
    event: (
      event: AnalyticsEvent,
      data?: AnalyticsEventData,
      verboseData?: AnalyticsEventVerboseData
    ): void => {
      const timestamp = new Date().toISOString();
      googleAnalyticsEvent(event, timestamp, data, session);
      trackAnalyticEvent(event, timestamp, data, verboseData, session);
    },
  };

  return contextType;
};

const AnalyticsContext: React.Context<AnalyticsContextType | null> =
  createContext<AnalyticsContextType | null>(null);

export const AnalyticsProvider = ({ children }: Props): React.ReactElement => {
  const analyticsContextType = AnalyticsHelper();

  return (
    <AnalyticsContext.Provider value={analyticsContextType}>{children}</AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextType => {
  const analyticsContext = useContext(AnalyticsContext);

  if (!analyticsContext) {
    throw new Error('useAnalytics must be used within a AnalyticsProvider');
  }

  return analyticsContext;
};
